import { useLocation } from "react-router-dom";
import { AtomEffect, atom, useRecoilState, useRecoilValue } from "recoil";

import { PublicationListOrderingValue } from "@/components/business/PublicationListContainer/publicationList.types";

import { RECOIL_KEYS } from "./recoilKeys";

import type { ProcessStatusCode } from "@/types/code.types";

export interface PublicationListSearchFilter {
  allInfo?: string;
  publicationIdInfo?: string;
  doiInfo?: string;
  titleInfo?: string;
  affiliationInfo?: string;
  allEditorInfo?: string;
  mcEditorInfo?: string;
  ftEditorInfo?: string;
}

export interface PublicationListFilter {
  search?: PublicationListSearchFilter;
  pageLimit: number;
  page: number;
  publicationListDataTotal: number;
  maxPage: number;
  topicType: string[];
  status: ProcessStatusCode[];
  publishable?: "true" | "false";
  isTranslated?: "true" | "false";
  order: {
    publicationId?: PublicationListOrderingValue;
    doi?: PublicationListOrderingValue;
    publicationDate?: PublicationListOrderingValue;
    publicationType?: PublicationListOrderingValue;
    topicType?: PublicationListOrderingValue;
    journalName?: PublicationListOrderingValue;
    volume?: PublicationListOrderingValue;
    issue?: PublicationListOrderingValue;
    title?: PublicationListOrderingValue;
    affiliationList?: PublicationListOrderingValue;
    status?: PublicationListOrderingValue;
    draftArticleStatusHighest?: PublicationListOrderingValue;
    articleRejectReason?: PublicationListOrderingValue;
    startDate?: PublicationListOrderingValue;
    completeDate?: PublicationListOrderingValue;
    mcEditor?: PublicationListOrderingValue;
    ftEditor?: PublicationListOrderingValue;
  };
}

const handleSearchQuery =
  (): AtomEffect<PublicationListFilter> =>
  ({ setSelf, onSet }) => {
    const newSearchParams = new URLSearchParams();
    const location = useLocation();

    if (location.search) {
      const searchParams = new URLSearchParams(location.search);

      const { topicType, status, search } = JSON.parse(
        Object.fromEntries(searchParams).filter,
      );

      setSelf({
        page: 1,
        maxPage: 1,
        pageLimit: 20,
        publicationListDataTotal: 0,
        topicType: topicType || [],
        status: status || [],
        order: {},
        search: search || {},
      });
    }
    onSet((newValue) => {
      if (window.location.pathname.includes("publication-list")) {
        newSearchParams.set("filter", JSON.stringify(newValue));
        window.history.pushState(
          "",
          "publication-list",
          `publication-list?${newSearchParams.toString()}`,
        );
      }
    });
  };

const publicationListFilterState = atom<PublicationListFilter>({
  key: RECOIL_KEYS.publicationListFilterState,
  default: {
    page: 1,
    maxPage: 1,
    pageLimit: 20,
    publicationListDataTotal: 0,
    topicType: [],
    status: [],
    order: {},
    search: {},
  },
  effects: [handleSearchQuery()],
});

export const usePublicationListFilterState = () =>
  useRecoilState(publicationListFilterState);

export const usePublicationListFilterValue = () =>
  useRecoilValue(publicationListFilterState);
